import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiService } from '../../lib/api';
import { useAdmin } from '../../hooks/useAdmin';
import { useAuth } from '../../hooks/useAuth';

interface User {
  id: number;
  email: string;
  username: string;
  userType: string;
  name?: string;
  country?: string;
  isAdmin: boolean;
  isSuperUser: boolean;
  isStaff: boolean;
  profilePicture?: string;
  lastSignedInAt?: string;
}

export default function UserList() {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [notification, setNotification] = useState<{type: 'success' | 'error', message: string} | null>(null);
  const [confirmAction, setConfirmAction] = useState<{userId: number, userName: string, action: 'promote' | 'demote' | 'delete'} | null>(null);
  const [editingUser, setEditingUser] = useState<{
    userId: number;
    username: string;
    userType: string;
    fullName: string;
    country: string;
    profilePicture?: string;
  } | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { requireAdmin } = useAdmin();
  const { startImpersonation, token } = useAuth();
  const queryClient = useQueryClient();

  // Require admin access
  requireAdmin();

  // Fetch all users
  const { data: users = [], isLoading, error } = useQuery<User[]>({
    queryKey: ['admin-users'],
    queryFn: async () => {
      return await apiService.request<User[]>('/api/v1/admin/users');
    }
  });

  // Promote user to admin
  const promoteMutation = useMutation({
    mutationFn: async (userId: number) => {
      return await apiService.request('/api/v1/admin/users/promote', {
        method: 'POST',
        body: JSON.stringify({ userId })
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    }
  });

  // Demote user from admin
  const demoteMutation = useMutation({
    mutationFn: async (userId: number) => {
      return await apiService.request('/api/v1/admin/users/demote', {
        method: 'POST',
        body: JSON.stringify({ userId })
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    }
  });

  // Update user profile
  const updateUserMutation = useMutation({
    mutationFn: async (data: {
      userId: number;
      username?: string;
      userType?: string;
      fullName?: string;
      country?: string;
      profilePicture?: string;
    }) => {
      return await apiService.request('/api/v1/admin/users/update', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    }
  });

  // Delete user
  const deleteUserMutation = useMutation({
    mutationFn: async (userId: number) => {
      return await apiService.request('/api/v1/admin/users/delete', {
        method: 'POST',
        body: JSON.stringify({ userId })
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
    }
  });

  const handlePromote = (userId: number, userName: string) => {
    setConfirmAction({userId, userName, action: 'promote'});
  };

  const handleDemote = (userId: number, userName: string) => {
    setConfirmAction({userId, userName, action: 'demote'});
  };

  const handleDelete = (userId: number, userName: string) => {
    setConfirmAction({userId, userName, action: 'delete'});
  };

  const handleEdit = (user: User) => {
    setEditingUser({
      userId: user.id,
      username: user.username || '',
      userType: user.userType,
      fullName: user.name || '',
      country: user.country || 'United States',
      profilePicture: user.profilePicture
    });
  };

  const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('media', file);

      const response = await fetch(`${import.meta.env.VITE_API_BASE_URL || 'http://localhost:3009'}/api/v1/media/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload image');
      }

      const { url } = await response.json();
      if (editingUser) {
        setEditingUser({ ...editingUser, profilePicture: url });
      }
    } catch (error) {
      console.error('Failed to upload image:', error);
      alert('Failed to upload image. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  const confirmPromotion = async () => {
    if (!confirmAction) return;

    try {
      await promoteMutation.mutateAsync(confirmAction.userId);
      setNotification({type: 'success', message: `${confirmAction.userName} has been promoted to admin`});
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      setNotification({type: 'error', message: `Failed to promote user: ${error?.message || 'Unknown error'}`});
      setTimeout(() => setNotification(null), 5000);
    }
    setConfirmAction(null);
  };

  const confirmDemotion = async () => {
    if (!confirmAction) return;

    try {
      await demoteMutation.mutateAsync(confirmAction.userId);
      setNotification({type: 'success', message: `${confirmAction.userName} has been demoted from admin`});
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      setNotification({type: 'error', message: `Failed to demote user: ${error?.message || 'Unknown error'}`});
      setTimeout(() => setNotification(null), 5000);
    }
    setConfirmAction(null);
  };

  const confirmDeletion = async () => {
    if (!confirmAction) return;

    try {
      await deleteUserMutation.mutateAsync(confirmAction.userId);
      setNotification({type: 'success', message: `${confirmAction.userName} has been deleted`});
      setTimeout(() => setNotification(null), 5000);
    } catch (error: any) {
      setNotification({type: 'error', message: `Failed to delete user: ${error?.message || 'Unknown error'}`});
      setTimeout(() => setNotification(null), 5000);
    }
    setConfirmAction(null);
  };

  const handleSaveEdit = async () => {
    if (!editingUser) return;

    try {
      await updateUserMutation.mutateAsync({
        userId: editingUser.userId,
        username: editingUser.username,
        userType: editingUser.userType,
        fullName: editingUser.fullName,
        country: editingUser.country,
        profilePicture: editingUser.profilePicture
      });
      setNotification({type: 'success', message: 'User profile updated successfully'});
      setTimeout(() => setNotification(null), 5000);
      setEditingUser(null);
    } catch (error: any) {
      setNotification({type: 'error', message: `Failed to update user: ${error?.message || 'Unknown error'}`});
      setTimeout(() => setNotification(null), 5000);
    }
  };

  const handleImpersonate = async (userId: number, userName: string) => {
    try {
      const response = await apiService.request<{
        success: boolean;
        impersonatedUser: {
          id: number;
          email: string;
          username: string;
          fullName: string;
          userType: string;
          isAdmin: boolean;
        };
      }>('/api/v1/admin/impersonate', {
        method: 'POST',
        body: JSON.stringify({ userId })
      });

      if (response.success && response.impersonatedUser) {
        // Start impersonation mode
        startImpersonation({
          id: response.impersonatedUser.id.toString(),
          email: response.impersonatedUser.email,
          name: response.impersonatedUser.fullName || response.impersonatedUser.username,
          userType: response.impersonatedUser.userType,
          isAdmin: response.impersonatedUser.isAdmin,
        });

        // Clear React Query cache to force fresh data load
        queryClient.clear();

        setNotification({type: 'success', message: `Now viewing as ${userName}. Redirecting...`});
        setTimeout(() => {
          // Force a full page reload to reset all state
          window.location.href = '/';
        }, 1000);
      }
    } catch (error: any) {
      setNotification({type: 'error', message: `Failed to impersonate user: ${error?.message || 'Unknown error'}`});
      setTimeout(() => setNotification(null), 5000);
    }
  };

  // Filter users based on search and type
  const filteredUsers = users.filter(user => {
    const matchesSearch =
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.name?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesType = filterType === 'all' || user.userType === filterType;

    return matchesSearch && matchesType;
  });

  // Count by type
  const counts = {
    all: users.length,
    surgeon: users.filter(u => u.userType === 'surgeon').length,
    staff: users.filter(u => u.userType === 'staff').length,
    rep: users.filter(u => u.userType === 'rep').length,
  };

  const getUserTypeColor = (type: string) => {
    switch (type) {
      case 'surgeon': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
      case 'staff': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300';
      case 'rep': return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  // Unused function - kept for potential future use
  // const getUserTypeIcon = (type: string) => {
  //   switch (type) {
  //     case 'surgeon': return '🩺';
  //     case 'staff': return '👔';
  //     case 'rep': return '💼';
  //     default: return '👤';
  //   }
  // };

  const formatLastSignedIn = (lastSignedInAt?: string) => {
    if (!lastSignedInAt) return 'Never';

    const date = new Date(lastSignedInAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="theme-card rounded-lg p-6 border-l-4 border-red-500">
          <p className="text-red-600 dark:text-red-400">Failed to load users. Please try again.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      {/* Notification Toast */}
      {notification && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg ${
          notification.type === 'success'
            ? 'bg-green-600 text-white'
            : 'bg-red-600 text-white'
        } animate-slide-in-right`}>
          <div className="flex items-center gap-2">
            <span>{notification.type === 'success' ? '✓' : '✕'}</span>
            <span>{notification.message}</span>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="theme-card rounded-lg p-6 max-w-md mx-4 w-full">
            <h3 className="text-lg font-bold theme-text mb-4">Edit User Profile</h3>

            {/* Profile Picture */}
            <div className="mb-4">
              <label className="block text-sm font-medium theme-text mb-2">Profile Picture</label>
              <div className="flex items-center gap-4">
                {editingUser.profilePicture ? (
                  <img
                    src={editingUser.profilePicture}
                    alt="Profile"
                    className="w-16 h-16 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                    <span className="text-2xl">👤</span>
                  </div>
                )}
                <label className="px-4 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 cursor-pointer">
                  {isUploading ? 'Uploading...' : 'Upload Image'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={isUploading}
                  />
                </label>
              </div>
            </div>

            {/* Username */}
            <div className="mb-4">
              <label className="block text-sm font-medium theme-text mb-2">Username</label>
              <input
                type="text"
                value={editingUser.username}
                onChange={(e) => {
                  // Remove non-alphanumeric characters only (allow mixed case)
                  const sanitized = e.target.value.replace(/[^a-zA-Z0-9]/g, '');
                  setEditingUser({ ...editingUser, username: sanitized });
                }}
                className="w-full px-4 py-2 theme-input rounded-lg"
                placeholder="letters and numbers only"
              />
              <p className="text-xs theme-text-muted mt-1">Only letters and numbers are allowed (case-insensitive)</p>
            </div>

            {/* Full Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium theme-text mb-2">Full Name</label>
              <input
                type="text"
                value={editingUser.fullName}
                onChange={(e) => setEditingUser({ ...editingUser, fullName: e.target.value })}
                className="w-full px-4 py-2 theme-input rounded-lg"
              />
            </div>

            {/* User Type */}
            <div className="mb-4">
              <label className="block text-sm font-medium theme-text mb-2">User Type</label>
              <select
                value={editingUser.userType}
                onChange={(e) => setEditingUser({ ...editingUser, userType: e.target.value })}
                className="w-full px-4 py-2 theme-input rounded-lg"
              >
                <option value="surgeon">🩺 Surgeon</option>
                <option value="staff">👔 Staff</option>
                <option value="rep">💼 Rep/Sales</option>
              </select>
            </div>

            {/* Location */}
            <div className="mb-6">
              <label className="block text-sm font-medium theme-text mb-2">Location</label>
              <input
                type="text"
                value={editingUser.country}
                onChange={(e) => setEditingUser({ ...editingUser, country: e.target.value })}
                className="w-full px-4 py-2 theme-input rounded-lg"
                placeholder="Enter location"
              />
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setEditingUser(null)}
                className="px-4 py-2 rounded-lg theme-text hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEdit}
                disabled={updateUserMutation.isPending}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 transition-colors"
              >
                {updateUserMutation.isPending ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {confirmAction && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="theme-card rounded-lg p-6 max-w-md mx-4">
            <h3 className="text-lg font-bold theme-text mb-4">Confirm Action</h3>
            <p className="theme-text mb-6">
              {confirmAction.action === 'promote'
                ? `Are you sure you want to promote ${confirmAction.userName} to admin?`
                : confirmAction.action === 'demote'
                ? `Are you sure you want to demote ${confirmAction.userName} from admin?`
                : `Are you sure you want to delete ${confirmAction.userName}? This action cannot be undone.`}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setConfirmAction(null)}
                className="px-4 py-2 rounded-lg theme-text hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={
                  confirmAction.action === 'promote' ? confirmPromotion :
                  confirmAction.action === 'demote' ? confirmDemotion :
                  confirmDeletion
                }
                disabled={promoteMutation.isPending || demoteMutation.isPending || deleteUserMutation.isPending}
                className={`px-4 py-2 rounded-lg text-white transition-colors disabled:opacity-50 ${
                  confirmAction.action === 'promote'
                    ? 'bg-yellow-600 hover:bg-yellow-700'
                    : confirmAction.action === 'delete'
                    ? 'bg-red-600 hover:bg-red-700'
                    : 'bg-gray-600 hover:bg-gray-700'
                }`}
              >
                {promoteMutation.isPending || demoteMutation.isPending || deleteUserMutation.isPending ? 'Processing...' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="mb-6">
        <h2 className="text-2xl font-bold theme-text mb-2">User Management</h2>
        <p className="theme-text-muted">View and manage all users in the system</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="theme-card rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm theme-text-muted">Total Users</p>
              <p className="text-2xl font-bold theme-text">{counts.all}</p>
            </div>
            <div className="text-3xl">👥</div>
          </div>
        </div>
        <div className="theme-card rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm theme-text-muted">Surgeons</p>
              <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">{counts.surgeon}</p>
            </div>
            <div className="text-3xl">🩺</div>
          </div>
        </div>
        <div className="theme-card rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm theme-text-muted">Staff</p>
              <p className="text-2xl font-bold text-green-600 dark:text-green-400">{counts.staff}</p>
            </div>
            <div className="text-3xl">👔</div>
          </div>
        </div>
        <div className="theme-card rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm theme-text-muted">Reps</p>
              <p className="text-2xl font-bold text-purple-600 dark:text-purple-400">{counts.rep}</p>
            </div>
            <div className="text-3xl">💼</div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="theme-card rounded-lg p-4 mb-6">
        <div className="flex flex-col md:flex-row gap-4">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <input
                type="text"
                placeholder="Search by email, username, or name..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full px-4 py-2 pl-10 theme-input rounded-lg"
              />
              <svg className="absolute left-3 top-3 h-5 w-5 theme-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Type Filter */}
          <div className="flex gap-2">
            <button
              onClick={() => setFilterType('all')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'all'
                  ? 'bg-red-600 text-white'
                  : 'theme-card theme-text hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              All ({counts.all})
            </button>
            <button
              onClick={() => setFilterType('surgeon')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'surgeon'
                  ? 'bg-blue-600 text-white'
                  : 'theme-card theme-text hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              🩺 Surgeons ({counts.surgeon})
            </button>
            <button
              onClick={() => setFilterType('staff')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'staff'
                  ? 'bg-green-600 text-white'
                  : 'theme-card theme-text hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              👔 Staff ({counts.staff})
            </button>
            <button
              onClick={() => setFilterType('rep')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filterType === 'rep'
                  ? 'bg-purple-600 text-white'
                  : 'theme-card theme-text hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              💼 Reps ({counts.rep})
            </button>
          </div>
        </div>
      </div>

      {/* User List */}
      <div className="theme-card rounded-lg overflow-hidden">
        {filteredUsers.length === 0 ? (
          <div className="p-12 text-center">
            <p className="theme-text-muted">No users found matching your criteria.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium theme-text-muted uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium theme-text-muted uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium theme-text-muted uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium theme-text-muted uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium theme-text-muted uppercase tracking-wider">
                    Location
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium theme-text-muted uppercase tracking-wider">
                    Username
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium theme-text-muted uppercase tracking-wider">
                    Last Signed In
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium theme-text-muted uppercase tracking-wider sticky right-0 bg-gray-50 dark:bg-gray-800">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          {user.profilePicture ? (
                            <img
                              src={user.profilePicture}
                              alt={user.name || user.username || 'User'}
                              className="h-10 w-10 rounded-full object-cover"
                            />
                          ) : (
                            <div className="h-10 w-10 bg-gradient-to-br from-healthy-green to-primary-600 rounded-full flex items-center justify-center">
                              <span className="text-white text-sm font-bold">
                                {(user.name || user.username || user.email || 'U').substring(0, 2).toUpperCase()}
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium theme-text">
                            {user.name || user.username || 'No name'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm theme-text">{user.email}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getUserTypeColor(user.userType)}`}>
                        {user.userType}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {user.isSuperUser ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300">
                            ⚡ Super User
                          </span>
                        ) : user.isAdmin ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300">
                            👑 Admin
                          </span>
                        ) : user.isStaff ? (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                            ⭐ Staff
                          </span>
                        ) : (
                          <span className="px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300">
                            User
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm theme-text">{user.country || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm theme-text-muted">{user.username || '-'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm theme-text" title={user.lastSignedInAt ? new Date(user.lastSignedInAt).toLocaleString() : 'Never signed in'}>
                        {formatLastSignedIn(user.lastSignedInAt)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm sticky right-0 bg-white dark:bg-gray-900">
                      <div className="flex items-center gap-2 flex-wrap">
                        {user.isSuperUser ? (
                          <span className="text-xs theme-text-muted italic">Super User (Protected)</span>
                        ) : (
                          <>
                            <button
                              onClick={() => handleEdit(user)}
                              className="px-3 py-1.5 bg-blue-600 text-white text-xs rounded-md hover:bg-blue-700 transition-colors font-medium"
                              title="Edit user"
                            >
                              ✏️ Edit
                            </button>
                            {user.isStaff && !user.isAdmin && (
                              <button
                                onClick={() => handlePromote(user.id, user.name || user.username || user.email)}
                                disabled={promoteMutation.isPending}
                                className="px-3 py-1.5 bg-yellow-600 text-white text-xs rounded-md hover:bg-yellow-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                              >
                                {promoteMutation.isPending ? '⏳' : '👑 Promote'}
                              </button>
                            )}
                            {user.isAdmin && (
                              <button
                                onClick={() => handleDemote(user.id, user.name || user.username || user.email)}
                                disabled={demoteMutation.isPending}
                                className="px-3 py-1.5 bg-gray-600 text-white text-xs rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors font-medium"
                              >
                                {demoteMutation.isPending ? '⏳' : '↓ Demote'}
                              </button>
                            )}
                            <button
                              onClick={() => handleImpersonate(user.id, user.name || user.username || user.email)}
                              className="px-3 py-1.5 bg-indigo-600 text-white text-xs rounded-md hover:bg-indigo-700 transition-colors font-medium"
                              title="View as this user"
                            >
                              🎭 View As
                            </button>
                            <button
                              onClick={() => handleDelete(user.id, user.name || user.username || user.email)}
                              className="px-3 py-1.5 bg-red-600 text-white text-xs rounded-md hover:bg-red-700 transition-colors font-medium"
                              title="Delete user"
                            >
                              🗑️ Delete
                            </button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Results count */}
      <div className="mt-4 text-center">
        <p className="text-sm theme-text-muted">
          Showing {filteredUsers.length} of {users.length} users
        </p>
      </div>
    </div>
  );
}
